<script lang="ts" setup>
import { Form, type FormSubmitEvent } from '@primevue/forms'
import { zodResolver } from '@primevue/forms/resolvers/zod'
import { useTranslation } from 'i18next-vue'
import { Button } from 'primevue'
import { useToast } from 'primevue/usetoast'
import { ref } from 'vue'
import { z } from 'zod'

import FormInput from '@/components/ui/FormInput.vue'
import { UserService } from '@/services/admin.ts'
import { useUserStore } from '@/stores/user-store.ts'
import type { IUser } from '@/types/shared.ts'

interface Props {
  data?: IUser
}

const props = defineProps<Props>()
const emit = defineEmits(['close'])
const userStore = useUserStore()
const toast = useToast()
const isSubmitting = ref<boolean>(false)
const { t } = useTranslation()

const resolver = zodResolver(
  z.object({
    email: z.string().email({ message: t('common:validators.email') }),
    nome: z.string().min(1, { message: t('common:validators.min', { min: 1 }) }),
    cognome: z.string().min(1, { message: t('common:validators.min', { min: 1 }) }),
    password: z.string().optional(),
  }),
)

const initialValues = ref({
  email: props.data?.email || '',
  nome: props.data?.nome || '',
  cognome: props.data?.cognome || '',
  password: '',
})

const onFormSubmit = async (event: FormSubmitEvent) => {
  if (!event.valid || !props.data?.id) return
  isSubmitting.value = true
  console.log('event', event)

  const values = Object.entries(event.states).reduce(
    (acc, [key, value]) => {
      acc[key as keyof typeof initialValues.value] = value.value
      return acc
    },
    {} as typeof initialValues.value,
  )

  const data = {
    id: props.data.id,
    email: values.email,
    nome: values.nome,
    cognome: values.cognome,
    password: values.password,
  }

  try {
    await UserService.update(props.data?.id!, data)
    await userStore.updateUserInfo(props.data?.id!)

    toast.add({
      severity: 'success',
      summary: t('common:toast.success.title'),
      detail: t('common:toast.success.description'),
      life: 3000,
    })
  } catch (error: unknown) {
    console.error('Submission error:', error)

    toast.add({
      severity: 'error',
      summary: t('common:toast.error.title'),
      detail: t('common:toast.error.description'),
      life: 3000,
    })
    isSubmitting.value = false
  } finally {
    isSubmitting.value = false
    emit('close')
  }
}

const closeDialogs = () => {
  emit('close')
}
</script>

<template>
  <Form
    :initial-values="initialValues"
    :resolver="resolver"
    class="flex flex-col gap-8 w-full mt-4"
    @submit="onFormSubmit"
  >
    <FormInput :label="t('common:user.email')" name="email" type="email" />
    <FormInput :label="t('common:user.name')" name="nome" type="text" />
    <FormInput :label="t('common:user.surname')" name="cognome" type="text" />
    <FormInput :label="t('common:user.password')" name="password" type="password" />

    <div class="flex justify-end gap-2">
      <Button
        icon="pi pi-times"
        :label="t('common:cancel')"
        text
        :disabled="isSubmitting"
        @click="closeDialogs"
      />
      <Button
        icon="pi pi-check"
        :label="t('common:submit')"
        type="submit"
        :loading="isSubmitting"
      />
    </div>
  </Form>
</template>

<style lang="scss" scoped></style>
